import { useState } from 'react';

const programs = [
  {
    id: 1,
    title: 'Awareness & Public Education',
    short: 'Education',
    icon: 'ri-book-open-line',
    image: 'https://readdy.ai/api/search-image?query=African%20teacher%20conducting%20epilepsy%20awareness%20session%20in%20a%20school%20classroom%2C%20students%20engaged%20and%20attentive%2C%20colorful%20educational%20posters%20on%20walls%2C%20bright%20natural%20lighting%2C%20authentic%20African%20school%20environment%2C%20hopeful%20atmosphere&width=800&height=600&seq=prog001&orientation=landscape',
    desc: 'Conducting quarterly school and community awareness drives, creating digital content, and community sensitization to disseminate accurate information about epilepsy.',
    activities: ['Quarterly school campaigns', 'Digital content creation', 'Community sensitization drives', 'Training 5,000 in seizure first-aid'],
  },
  {
    id: 2,
    title: 'Patient Support & Empowerment',
    short: 'Support',
    icon: 'ri-hand-heart-line',
    image: 'https://readdy.ai/api/search-image?query=Supportive%20group%20therapy%20session%20for%20epilepsy%20patients%20in%20Africa%2C%20diverse%20group%20of%20adults%20sitting%20in%20circle%2C%20compassionate%20facilitator%2C%20warm%20indoor%20lighting%2C%20community%20center%20setting%2C%20emotional%20support%20and%20healing%20atmosphere&width=800&height=600&seq=prog002&orientation=landscape',
    desc: 'Facilitating support groups, providing essential medication assistance, and offering counselling services to empower patients and their families.',
    activities: ['10 community support groups', 'Medication assistance programs', 'Counselling & mental health support', 'Skills-building programs'],
  },
  {
    id: 3,
    title: 'Medical Assistance & Capacity Building',
    short: 'Medical',
    icon: 'ri-stethoscope-line',
    image: 'https://readdy.ai/api/search-image?query=African%20healthcare%20workers%20receiving%20specialized%20epilepsy%20training%20in%20a%20modern%20medical%20facility%2C%20doctors%20and%20nurses%20in%20professional%20attire%2C%20medical%20equipment%20visible%2C%20collaborative%20learning%20environment%2C%20bright%20clinical%20setting&width=800&height=600&seq=prog003&orientation=landscape',
    desc: 'Forging hospital partnerships, organizing medical outreaches, and delivering specialized training programs for healthcare workers to enhance epilepsy management.',
    activities: ['Annual medical outreaches', 'Healthcare worker training', 'Hospital partnership programs', '500 healthcare workers trained'],
  },
  {
    id: 4,
    title: 'Advocacy & Policy Influence',
    short: 'Advocacy',
    icon: 'ri-government-line',
    image: 'https://readdy.ai/api/search-image?query=African%20policy%20meeting%20and%20advocacy%20session%20for%20healthcare%20reform%2C%20diverse%20group%20of%20professionals%20and%20government%20officials%20in%20conference%20room%2C%20formal%20setting%2C%20collaborative%20discussion%2C%20modern%20African%20office%20environment&width=800&height=600&seq=prog004&orientation=landscape',
    desc: 'Publishing whitepapers, engaging in dialogues with stakeholders, conducting research, and collaborating with government bodies for improved epilepsy policies.',
    activities: ['Policy whitepaper publications', 'Government stakeholder engagement', 'Research & data development', 'National epilepsy report'],
  },
  {
    id: 5,
    title: 'Research & Data Development',
    short: 'Research',
    icon: 'ri-bar-chart-line',
    image: 'https://readdy.ai/api/search-image?query=African%20researchers%20and%20data%20analysts%20working%20on%20epilepsy%20health%20data%20in%20a%20modern%20research%20facility%2C%20computers%20and%20charts%20visible%2C%20collaborative%20team%20environment%2C%20professional%20setting%2C%20focused%20and%20determined%20atmosphere&width=800&height=600&seq=prog005&orientation=landscape',
    desc: 'Establishing robust epilepsy data reporting mechanisms and comprehensive community mapping initiatives to inform evidence-based interventions.',
    activities: ['National epilepsy data system', 'Community mapping initiatives', 'Evidence-based research', 'Impact measurement frameworks'],
  },
];

export default function ProgramsSection() {
  const [active, setActive] = useState(0);
  const prog = programs[active];

  return (
    <section className="py-24 bg-[#FAF7FC]" id="pillars">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-4">
          <span className="text-xs font-semibold uppercase tracking-[4px] text-[#7B2D8B]">What We Do</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 text-center mb-4">
          Pillars of <span className="text-[#7B2D8B]">Progress</span>
        </h2>
        <p className="text-gray-500 text-center max-w-xl mx-auto mb-14 leading-relaxed">
          Our five core programs work in concert to deliver comprehensive, sustainable change for persons living with epilepsy.
        </p>

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {programs.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setActive(i)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                active === i
                  ? 'bg-[#7B2D8B] text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-[#7B2D8B] hover:text-[#7B2D8B]'
              }`}
            >
              <i className={`${p.icon} mr-2`}></i>
              {p.short}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 bg-white rounded-3xl overflow-hidden border border-purple-100">
          <div className="h-72 lg:h-auto">
            <img
              src={prog.image}
              alt={prog.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="p-10 flex flex-col justify-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#7B2D8B]/10 mb-5">
              <i className={`${prog.icon} text-[#7B2D8B] text-2xl`}></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{prog.title}</h3>
            <p className="text-gray-500 leading-relaxed mb-6 text-sm">{prog.desc}</p>
            <ul className="space-y-2">
              {prog.activities.map((act) => (
                <li key={act} className="flex items-center gap-3 text-sm text-gray-700">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#7B2D8B]/10 shrink-0">
                    <i className="ri-check-line text-[#7B2D8B] text-xs"></i>
                  </span>
                  {act}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
